// "shire" / "rivendell" easter egg — reskins the whole Backoffice into a calm, ultra-premium
// woodland sanctuary: soft forest greens, warm wood-brown parchment, and an elegant serif. It works by
// overriding Mantine's own CSS custom properties in one injected <style> (both light & dark schemes),
// so every surface shifts at once. Purely cosmetic and fully reversible — Esc, or a "stop"/"enough"
// word, removes the style and everything snaps back. No sound, no dependencies.
const STYLE_ID = 'shire-theme';
const KEY = 'shire_theme'; // persisted so a user who likes it keeps it across reloads
const DISCOVERED_KEY = 'shire_discovered'; // gates the God Mode control until the egg is found
const Z = 9990;

export type ShireVariant = 'shire' | 'rivendell';

// The God Mode toggle stays hidden until the user has discovered the theme by typing the word — so it
// never spoils the egg's existence to someone who only found God Mode.
export const isShireDiscovered = (): boolean => {
    try { return localStorage.getItem(DISCOVERED_KEY) === '1'; } catch { return false; }
};
export const markShireDiscovered = (): void => {
    try { localStorage.setItem(DISCOVERED_KEY, '1'); } catch { /* ignore */ }
};

// This app doesn't use Mantine's stock palette — theme.ts builds every surface from custom named
// colors (sidebarBlue, fixedLightGray, dark, darkBlue, …) wired through virtualColor, and buttons
// default to color="blue". So a woodland reskin means remapping those *base* colour scales; the
// virtual colours (navbar/body/header/section/font) then follow automatically, per scheme.
const tuple = (c: string): string[] => Array(10).fill(c);
const scaleVars = (name: string, arr: string[]): string =>
    arr.map((c, i) => `--mantine-color-${name}-${i}:${c};`).join('');

// Accent scales (base "blue" → primary in light + all color="blue" buttons; "lightGray" → primary in dark).
const GREEN = ['#eef5ec', '#d8e9d2', '#b7d8ad', '#94c686', '#79b869', '#5aa14d', '#47913b', '#3a7a32', '#2f6628', '#234f1f'];
const MOSS = ['#f0f5ee', '#dcebd6', '#c0dcb6', '#a3cd96', '#8cbf7d', '#79b169', '#6a9e5c', '#537c48', '#426238', '#324a2b'];
const TEAL = ['#eaf4f0', '#cfe8e0', '#aad6c9', '#83c3b1', '#66b39d', '#4fa189', '#3f8a76', '#347363', '#2b5f52', '#204a40'];
const MOSST = ['#eef5f2', '#d6ebe4', '#b4dccf', '#8fcbb9', '#74bda7', '#5fae97', '#549b86', '#427b6b', '#356355', '#284c41'];

// Softened, earthy semantic tints — muted so alerts sit calmly in the theme but stay legible/distinct.
const CLAY = ['#f7ece9', '#ecd3cc', '#dcb2a7', '#cc9082', '#c07866', '#b5634f', '#a9563f', '#8f4433', '#763829', '#5e2c20']; // danger / red
const AMBER = ['#f8f0dd', '#eeddb8', '#e1c488', '#d4ab5b', '#c99a3e', '#b98a34', '#a2792d', '#836223', '#66491a', '#4c3612']; // warning / yellow / orange
const LEAF = ['#eef5ea', '#d9ead0', '#bedcac', '#a1cd86', '#8bbf6c', '#79b158', '#699e4a', '#527c3a', '#41622e', '#314a23']; // success / green
const SLATE = ['#eef2f6', '#d5e0ea', '#b3c6d8', '#8fa9c3', '#7593b3', '#5e80a4', '#4f7096', '#3f5a79', '#334862', '#26374c']; // info (cyan / indigo)

// Per-variant replacements for Mantine's built-in "dark" scale — used by every Card/Paper/input in
// dark mode, so this is what makes the two worlds differ (and carry visible hue) after dark. The
// light→dark luminance ramp is preserved so foreground/border/background roles still hold.
const DARK_SHIRE = ['#ece2ce', '#d6cbb1', '#b7ac8e', '#938a6b', '#66604a', '#4b4636', '#3a3222', '#282013', '#1b160d', '#120e07'];    // warm timber
const DARK_RIVENDELL = ['#dfe8ea', '#c4d3d6', '#a1b6bb', '#7c959c', '#54707a', '#3d545c', '#2c3f46', '#1d2e34', '#152329', '#0d181c']; // cool twilight

// A full, distinct surface palette per variant (single tone per theme virtualColor) so the two worlds
// look nothing alike: Shire = warm wood/gold/meadow, Rivendell = cool teal/silver/twilight.
type SurfaceSet = { light: Record<string, string>; dark: Record<string, string> };
const SURFACE: Record<ShireVariant, SurfaceSet> = {
    // The Shire — cosy hobbit countryside: brown timber, golden parchment, green meadows.
    shire: {
        light: {
            navbarBackgroundColor: '#5e4326',    // the menu (warm timber)
            bodyBackgroundColor: '#efe2c4',      // main background (golden parchment)
            sidebarBackgroundColor: '#f3ead2',   // panels / tab bodies
            sectionColor: '#f0e6ca',
            sectionColorVariant: '#e6d8b2',
            headerColor: '#4a7c3f',              // top bar (meadow green)
            fontColor: '#3a2e18',                // text (dark cocoa)
        },
        dark: {
            navbarBackgroundColor: '#33291a',    // visible warm timber
            bodyBackgroundColor: '#17130b',
            sidebarBackgroundColor: '#241d13',
            sectionColor: '#211a10',
            sectionColorVariant: '#2c2213',
            headerColor: '#31491f',              // moss green (reads clearly)
            fontColor: '#ecdfc4',
        },
    },
    // Rivendell — ethereal elven sanctuary: slate teal, silvered mist, twilight.
    rivendell: {
        light: {
            navbarBackgroundColor: '#37545a',    // the menu (slate teal)
            bodyBackgroundColor: '#e6eef0',      // main background (silver-blue mist)
            sidebarBackgroundColor: '#eef4f4',
            sectionColor: '#e7f0f0',
            sectionColorVariant: '#d8e8e8',
            headerColor: '#2b4f57',              // top bar (twilight teal)
            fontColor: '#23343a',                // text (cool slate)
        },
        dark: {
            navbarBackgroundColor: '#17272d',    // visible teal-blue
            bodyBackgroundColor: '#0d1519',
            sidebarBackgroundColor: '#16242a',
            sectionColor: '#132025',
            sectionColorVariant: '#183029',
            headerColor: '#164049',              // twilight teal (reads clearly)
            fontColor: '#d8e7e6',
        },
    },
};

interface Accent { primary: string[]; primaryDark: string[]; darkBase: string[]; anchorLight: string; anchorDark: string; }
const ACCENTS: Record<ShireVariant, Accent> = {
    shire: { primary: GREEN, primaryDark: MOSS, darkBase: DARK_SHIRE, anchorLight: '#3f6b2e', anchorDark: '#c0dcb6' },
    rivendell: { primary: TEAL, primaryDark: MOSST, darkBase: DARK_RIVENDELL, anchorLight: '#2f6f74', anchorDark: '#b4dccf' },
};

const SERIF = "'Palatino Linotype','Book Antiqua',Palatino,'Iowan Old Style',Georgia,serif";

// A single-tone surface colour applied to every place Mantine might read it: the BARE virtual var
// (e.g. AppShell.Navbar uses `var(--mantine-color-navbarBackgroundColor)` with no shade), all shades,
// and the filled/light variants.
const solid = (name: string, color: string): string =>
    `--mantine-color-${name}:${color};` +
    scaleVars(name, tuple(color)) +
    `--mantine-color-${name}-filled:${color};--mantine-color-${name}-filled-hover:${color};` +
    `--mantine-color-${name}-light:${color};--mantine-color-${name}-light-hover:${color};`;

// An accent colour scale (primary + color="blue") with filled/light variants derived from the ramp.
const accent = (name: string, scale: string[], filled: number, text: number): string =>
    `--mantine-color-${name}:${scale[filled]};` +
    scaleVars(name, scale) +
    `--mantine-color-${name}-filled:${scale[filled]};--mantine-color-${name}-filled-hover:${scale[filled + 1]};` +
    `--mantine-color-${name}-light:${scale[filled]}22;--mantine-color-${name}-light-hover:${scale[filled]}33;--mantine-color-${name}-light-color:${scale[text]};`;

const TITLES: Record<ShireVariant, { title: string; subtitle: string }> = {
    shire: { title: '🌿 The Shire', subtitle: 'Peace settles over the Backoffice.' },
    rivendell: { title: '🍃 Rivendell', subtitle: 'The Last Homely House welcomes you.' },
};

let styleEl: HTMLStyleElement | null = null;
let vignette: HTMLDivElement | null = null;
let current: ShireVariant | null = null;

const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') stopShire();
};

const surfaces = (map: Record<string, string>): string =>
    Object.entries(map).map(([name, color]) => solid(name, color)).join('');

const buildCss = (variant: ShireVariant): string => {
    const a = ACCENTS[variant];

    // Every override is scoped under the color-scheme attribute (specificity 0,1,1) so it outranks
    // Mantine's own :root variables no matter which <style> the browser sees last. We override the
    // *resolved* virtualColor variables directly (the exact vars the components read), plus the
    // primary/blue accent scales, per scheme.
    const shared =
        `--mantine-font-family:${SERIF};--mantine-font-family-headings:${SERIF};` +
        accent('blue', a.primary, 6, 7) +               // all color="blue" buttons/loaders
        `--mantine-primary-color-filled:${a.primary[6]};--mantine-primary-color-filled-hover:${a.primary[7]};` +
        `--mantine-primary-color-light:${a.primary[6]}22;--mantine-primary-color-light-hover:${a.primary[6]}33;--mantine-primary-color-light-color:${a.primary[7]};` +
        // Softened semantic palette (both the app's danger/warning/success and Mantine's stock names).
        accent('red', CLAY, 6, 7) + accent('danger', CLAY, 6, 7) +
        accent('yellow', AMBER, 6, 7) + accent('orange', AMBER, 6, 7) + accent('warning', AMBER, 6, 7) +
        accent('green', LEAF, 6, 7) + accent('success', LEAF, 6, 7) +
        accent('cyan', SLATE, 6, 7) + accent('indigo', SLATE, 6, 7);

    const light =
        'html[data-mantine-color-scheme="light"]{' +
        shared + surfaces(SURFACE[variant].light) +
        accent('primaryColor', a.primary, 6, 7) +
        `--mantine-color-anchor:${a.anchorLight};` +
        '}';

    const dark =
        'html[data-mantine-color-scheme="dark"]{' +
        shared + surfaces(SURFACE[variant].dark) +
        accent('blue', a.primary, 7, 3) +               // slightly brighter fill for dark
        accent('primaryColor', a.primaryDark, 6, 2) +
        scaleVars('dark', a.darkBase) +                 // dark-mode Card/Paper/input/border surfaces
        `--mantine-primary-color-filled:${a.primaryDark[6]};--mantine-primary-color-filled-hover:${a.primaryDark[5]};` +
        `--mantine-color-anchor:${a.anchorDark};` +
        '}';

    // Belt-and-braces: paint the big structural surfaces directly by their (stable) Mantine class.
    // `bg="navbarBackgroundColor"` becomes an inline style var whose exact resolution is fiddly to
    // match; an `!important` stylesheet rule beats a non-important inline style, so this always wins.
    const paint = (mode: 'light' | 'dark', s: Record<string, string>): string => {
        const sel = `html[data-mantine-color-scheme="${mode}"]`;
        return `${sel} .mantine-AppShell-navbar{background:${s.navbarBackgroundColor}!important;}` +
            `${sel} .mantine-AppShell-root{background:${s.bodyBackgroundColor}!important;}` +
            `${sel} .mantine-AppShell-main{background:${s.bodyBackgroundColor}!important;}` +
            `${sel} .mantine-AppShell-header{background:${s.headerColor}!important;}`;
    };

    // A couple of dashboard cards hardcode an inline rgb() background (no CSS var to override), so we
    // match the inline style string directly; an !important rule beats a non-important inline style.
    const hardcoded =
        `[style*="rgb(50, 31, 219)"]{background-color:${SLATE[7]}!important;}` +  // Open Lead Fees (blue)
        `[style*="rgb(255, 193, 7)"]{background-color:${AMBER[6]}!important;}`;   // Protest (yellow)

    // Gentle morph as surfaces recolour.
    const morph = 'body,.mantine-AppShell-header,.mantine-AppShell-navbar,.mantine-Paper-root,.mantine-Card-root{transition:background-color 900ms ease,color 900ms ease,border-color 900ms ease;}';

    return light + dark + paint('light', SURFACE[variant].light) + paint('dark', SURFACE[variant].dark) + hardcoded + morph;
};

// A soft green vignette that lingers for ambiance (very low alpha, never blocks the UI).
const addVignette = (variant: ShireVariant) => {
    const tint = variant === 'rivendell' ? 'rgba(40,80,72,0.16)' : 'rgba(46,82,50,0.16)';
    vignette = document.createElement('div');
    vignette.style.cssText =
        `position:fixed;inset:0;z-index:${Z};pointer-events:none;opacity:0;transition:opacity 1200ms ease;` +
        `background:radial-gradient(ellipse at center, rgba(0,0,0,0) 55%, ${tint} 100%);`;
    document.body.appendChild(vignette);
    requestAnimationFrame(() => { if (vignette) vignette.style.opacity = '1'; });
};

const showTitle = (variant: ShireVariant) => {
    const t = TITLES[variant];
    const el = document.createElement('div');
    el.style.cssText =
        `position:fixed;inset:0;z-index:${Z + 2};pointer-events:none;opacity:0;transition:opacity 1400ms ease;` +
        'display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;' +
        `background:radial-gradient(ellipse 640px 340px at center, rgba(12,20,12,0.55) 0%, rgba(12,20,12,0) 72%);font-family:${SERIF};`;
    el.innerHTML =
        `<div style="font:600 46px ${SERIF};letter-spacing:1px;color:#f2ead3;text-shadow:0 2px 26px rgba(0,0,0,0.85);">${t.title}</div>` +
        `<div style="margin-top:10px;font:400 21px ${SERIF};font-style:italic;color:#d7e4cf;text-shadow:0 2px 20px rgba(0,0,0,0.85);">${t.subtitle}</div>`;
    document.body.appendChild(el);
    requestAnimationFrame(() => { el.style.opacity = '1'; });
    window.setTimeout(() => { el.style.opacity = '0'; }, 4200);
    window.setTimeout(() => el.remove(), 5800);
};

export const isShireActive = (): boolean => !!styleEl;
export const getShireVariant = (): ShireVariant | null => current;

// silent — re-apply on page load / from the God Mode toggle without the dramatic entrance title.
export function startShire(variant: ShireVariant = 'shire', opts?: { silent?: boolean }): void {
    if (typeof document === 'undefined') return;
    current = variant;
    try { localStorage.setItem(KEY, variant); } catch { /* ignore */ }
    // Re-typing swaps the variant (Shire ⇄ Rivendell) rather than stacking.
    styleEl?.remove();
    styleEl = document.createElement('style');
    styleEl.id = STYLE_ID;
    styleEl.textContent = buildCss(variant);
    document.head.appendChild(styleEl);

    if (!vignette) addVignette(variant);
    if (!opts?.silent) showTitle(variant);
    window.addEventListener('keydown', onKey);
}

export function stopShire(): void {
    current = null;
    try { localStorage.removeItem(KEY); } catch { /* ignore */ }
    if (!styleEl) return;
    window.removeEventListener('keydown', onKey);
    styleEl.remove();
    styleEl = null;
    if (vignette) {
        const v = vignette;
        vignette = null;
        v.style.opacity = '0';
        window.setTimeout(() => v.remove(), 900);
    }
}

// Called once on app load: quietly restore the theme if the user chose to keep it.
export function applyPersistedShire(): void {
    if (typeof document === 'undefined' || styleEl) return;
    let v: string | null = null;
    try { v = localStorage.getItem(KEY); } catch { /* ignore */ }
    if (v === 'shire' || v === 'rivendell') startShire(v, { silent: true });
}
